import React, { useMemo, useState } from "react";
import {
  FaBed,
  FaCalendarAlt,
  FaDoorOpen,
  FaPlane,
  FaSuitcase,
  FaUsers,
} from "react-icons/fa";
import { useAppContext } from "../../contexts/AppContext";
import { formatIls } from "../packageProfileMockup/mockPackageProfileData";
import {
  convertChargeIlsToDisplayCurrency,
  formatHubwayzSaleDisplay,
} from "./hubwayz/hubwayzRoomSaleDisplay";
import { formatHubwayzSupplierRateLabel } from "./hubwayz/hubwayzSelectedOfferCurrency";

function formatDdMmYyyy(raw) {
  const s = String(raw || "").trim();
  if (!s) return "";
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) return s;
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

function formatUsdToIlsRateLabel(rate) {
  const n = Number(rate);
  if (!Number.isFinite(n) || n <= 0) return null;
  return `1 USD = ₪${n.toLocaleString("he-IL", { maximumFractionDigits: 4 })}`;
}

function airlineCodeFromLeg(leg) {
  if (leg?.airlineCode) return String(leg.airlineCode).trim().toUpperCase();
  const fromNo = String(leg?.flightNo || "")
    .trim()
    .split(/\s+/)[0];
  if (fromNo && /^[A-Z0-9]{2}$/i.test(fromNo)) return fromNo.toUpperCase();
  return null;
}

/**
 * Package-profile HPM order summary for טיסה + מלון.
 * Visual SSOT = BookingSidebarSummary layoutVariant="hpm".
 */
export default function UnifiedFlightHotelOrderSummary({
  hotelName,
  city,
  country,
  checkIn,
  checkOut,
  dateRangeLabel,
  nights,
  roomsCount = 1,
  adultsCount,
  childrenCount = 0,
  roomName,
  boardLabel,
  baggageLabel,
  baggageItems = null,
  outbound,
  inbound,
  flightOptionLabel,
  totalFx,
  currency = "USD",
  /** Search currency chosen by the agent. Not the supplier currency. */
  displayCurrency = null,
  /** Hotels-only Hubwayz ILS quote. Supplier amount is not rewritten. */
  customerPrice = null,
  baggageTotalFx = 0,
  termsChecked = false,
  onTermsChange,
  showTerms = true,
  /** When false, hide terms + CTA block (campaign sticky summary). */
  showActions = true,
  primaryDisabled = false,
  primaryDisabledHint = null,
  primaryLabel = "המשך לתשלום",
  onPrimary,
  deferredLabel = null,
  onDeferred,
  secondaryLabel = "חזרה",
  onSecondary,
  /** agent sees the commission split. customer sees only the sale total. */
  priceAudience = "agent",
}) {
  const { currencyRate, currencyRateSource } = useAppContext();
  const [flightDetailsOpen, setFlightDetailsOpen] = useState(false);

  const destinationDisplay = [city, country].filter(Boolean).join(", ") || city || "";

  const stayLine = useMemo(() => {
    const depDate = formatDdMmYyyy(checkIn);
    const retDate = formatDdMmYyyy(checkOut);
    const datePart =
      depDate && retDate ? `${depDate} – ${retDate}` : dateRangeLabel || "—";
    const nightsPart =
      nights != null && Number(nights) > 0 ? `${Number(nights)} לילות` : null;
    return [datePart, nightsPart].filter(Boolean).join(" · ");
  }, [checkIn, checkOut, dateRangeLabel, nights]);

  const occupancyLine = useMemo(() => {
    const adults = Math.max(1, Number(adultsCount) || 2);
    const kids = Math.max(0, Number(childrenCount) || 0);
    const rooms = Math.max(1, Number(roomsCount) || 1);
    const paxParts = [`${adults} מבוגרים`];
    if (kids > 0) paxParts.push(`${kids} ילדים`);
    return `${rooms} חדר${rooms > 1 ? "ים" : ""}, ${paxParts.join(", ")}`;
  }, [adultsCount, childrenCount, roomsCount]);

  const airlineLabel =
    airlineCodeFromLeg(outbound) ||
    airlineCodeFromLeg(inbound) ||
    outbound?.airline ||
    inbound?.airline ||
    "טיסות";

  const serverQuote = customerPrice?.source === "server_quote";
  const pricedOk = serverQuote
    ? Number.isFinite(Number(customerPrice?.agentNetIls)) && Number(customerPrice.agentNetIls) > 0
    : customerPrice?.ok === true;
  const fxBlocked =
    customerPrice != null &&
    customerPrice.ok === false &&
    (customerPrice.reason === "unrecognized_currency" ||
      customerPrice.reason === "missing_fx_rate" ||
      customerPrice.reason === "invalid_fx_rate");

  const usdRate = Number(currencyRate?.USD);
  const totalUsd =
    pricedOk || fxBlocked
      ? null
      : String(currency || "").toUpperCase() === "USD"
        ? Number(totalFx)
        : Number.isFinite(usdRate) && usdRate > 0
          ? Number(totalFx) / usdRate
          : Number(totalFx);

  const agentNetIls = pricedOk
    ? Number(customerPrice.agentNetIls ?? customerPrice.chargeIls)
    : null;
  const agentMarkupIls = pricedOk ? Number(customerPrice.agentMarkupIls) || 0 : 0;
  const orderNetAmount = Number(customerPrice?.agentNetAmount);
  const orderMarkupAmount = Number(customerPrice?.agentMarkupAmount);
  const orderSaleAmount = Number(customerPrice?.customerSaleAmount);
  const orderCurrency = String(
    customerPrice?.agentNetCurrency || customerPrice?.customerSaleCurrency || ""
  )
    .trim()
    .toUpperCase();
  const hasOrderMoney =
    pricedOk &&
    !!orderCurrency &&
    Number.isFinite(orderNetAmount) &&
    Number.isFinite(orderMarkupAmount) &&
    Number.isFinite(orderSaleAmount);
  const quotedCustomerIls = Number(
    customerPrice?.customerSaleIls ?? customerPrice?.customerPayIls ?? agentNetIls
  );
  const totalIls =
    serverQuote || fxBlocked || pricedOk
      ? null
      : Number.isFinite(usdRate) && usdRate > 0 && Number.isFinite(Number(totalUsd))
        ? Number(totalUsd) * usdRate
        : null;

  const customerIls = pricedOk && Number.isFinite(quotedCustomerIls) ? quotedCustomerIls : totalIls;
  const shownIls = Number.isFinite(customerIls) ? Math.round(customerIls) : null;
  const searchCode = String(
    displayCurrency || customerPrice?.agentPriceCurrency || currency || ""
  )
    .trim()
    .toUpperCase();
  const lockedDealAmount = Number(customerPrice?.dealAmount);
  const lockedDealCurrency = String(customerPrice?.dealCurrency || "").trim().toUpperCase();
  const agentSearch = hasOrderMoney
    ? { ok: true, amount: orderNetAmount, currency: orderCurrency }
    : pricedOk
    ? lockedDealAmount > 0 && lockedDealCurrency
      ? { ok: true, amount: lockedDealAmount, currency: lockedDealCurrency }
      : Number(customerPrice.agentPriceAmount) > 0 && customerPrice.agentPriceCurrency
        ? {
            ok: true,
            amount: Number(customerPrice.agentPriceAmount),
            currency: String(customerPrice.agentPriceCurrency).toUpperCase(),
          }
        : convertChargeIlsToDisplayCurrency({
            chargeIls: agentNetIls,
            displayCurrency: searchCode,
            currencyRate,
            currencyRateSource,
          })
    : null;
  const orderMarkupHidesIls = hasOrderMoney && orderMarkupAmount === 0 && agentMarkupIls > 0;
  const orderSaleHidesMarkup =
    hasOrderMoney &&
    agentMarkupIls > 0 &&
    Math.abs(orderSaleAmount - orderNetAmount) < 0.0001;
  const commissionSearch = hasOrderMoney && !orderMarkupHidesIls
    ? { ok: true, amount: orderMarkupAmount, currency: orderCurrency }
    : pricedOk && agentMarkupIls > 0
      ? convertChargeIlsToDisplayCurrency({
          chargeIls: agentMarkupIls,
          displayCurrency: searchCode,
          currencyRate,
          currencyRateSource,
        })
      : null;
  const showAgentBreakdown =
    priceAudience !== "customer" &&
    pricedOk &&
    (agentMarkupIls > 0 || hasOrderMoney || customerPrice?.commissionLocked === true);
  const rateSource = pricedOk
    ? convertChargeIlsToDisplayCurrency({
        chargeIls: customerIls,
        displayCurrency: searchCode,
        currencyRate,
        currencyRateSource,
      })
    : null;
  const customerUsd =
    searchCode === "USD" && hasOrderMoney && orderCurrency === "USD" && !orderSaleHidesMarkup
      ? { ok: true, amount: orderSaleAmount, currency: "USD" }
      : searchCode === "USD" && shownIls > 0
      ? convertChargeIlsToDisplayCurrency({
          chargeIls: shownIls,
          displayCurrency: "USD",
          currencyRate,
          currencyRateSource,
        })
      : null;
  const customerUsdLabel =
    customerUsd?.ok === true && customerUsd.currency === "USD"
      ? formatHubwayzSaleDisplay(customerUsd.amount, "USD")
      : "";
  const rateLabel = fxBlocked
    ? ""
    : rateSource?.ok === true && rateSource.fxApplied
      ? formatHubwayzSupplierRateLabel(rateSource.currency, rateSource.rateUsed)
      : formatUsdToIlsRateLabel(usdRate) || "";

  const termsOk = !showTerms || !!termsChecked;
  const continueDisabled = !termsOk || !!primaryDisabled || fxBlocked;
  const disableHint = fxBlocked
    ? "לא ניתן להמשיך לתשלום: מטבע הספק לא מוכר או שאין שער המרה תקין."
    : !termsOk
      ? "יש לאשר את תנאי ההזמנה כדי להמשיך"
      : primaryDisabledHint;

  const breakdown =
    Number(baggageTotalFx) > 0
      ? [
          {
            key: "baggage",
            label: "כבודה",
            valueText:
              String(currency || "").toUpperCase() === "USD"
                ? `$${Math.round(Number(baggageTotalFx)).toLocaleString("en-US")}`
                : formatIls(
                    Number.isFinite(usdRate) && usdRate > 0
                      ? Number(baggageTotalFx) * usdRate
                      : Number(baggageTotalFx)
                  ),
          },
        ]
      : [];

  return (
    <div className="hpm-summary__card">
      <p className="hpm-summary__eyebrow">סיכום ההזמנה</p>
      <h2 className="hpm-summary__hotel">
        {hotelName ? (
          <bdi dir="ltr" lang="en">
            {hotelName}
          </bdi>
        ) : outbound || inbound ? (
          "טיסה + מלון"
        ) : (
          "מלון"
        )}
      </h2>
      {destinationDisplay ? (
        <p className="hpm-summary__dest">{destinationDisplay}</p>
      ) : null}

      <div className="hpm-summary__facts" aria-label="פרטי החופשה">
        <div className="hpm-summary__fact">
          <FaCalendarAlt aria-hidden className="hpm-summary__fact-icon" />
          <span>{stayLine}</span>
        </div>
        <div className="hpm-summary__fact">
          <FaUsers aria-hidden className="hpm-summary__fact-icon" />
          <span>{occupancyLine}</span>
        </div>
        {roomName ? (
          <div className="hpm-summary__fact">
            <FaDoorOpen aria-hidden className="hpm-summary__fact-icon" />
            <span>
              <bdi dir="ltr" lang="en">
                {roomName}
              </bdi>
            </span>
          </div>
        ) : null}
        {boardLabel ? (
          <div className="hpm-summary__fact">
            <FaBed aria-hidden className="hpm-summary__fact-icon" />
            <span>{boardLabel}</span>
          </div>
        ) : null}
        {Array.isArray(baggageItems) && baggageItems.length ? (
          <div className="hpm-summary__fact hpm-summary__fact--baggage">
            <FaSuitcase aria-hidden className="hpm-summary__fact-icon" />
            <ul className="hpm-summary__baggage-list">
              {baggageItems.map((item, idx) => (
                <li key={`${item.direction || "bag"}-${item.title}-${idx}`}>
                  {item.direction ? <em>{item.direction}</em> : null}{" "}
                  <strong>{item.title}</strong>
                  {item.paxCount > 0 ? ` · ${item.paxCount} נוסעים` : ""}
                  {item.priceLabel ? ` · ${item.priceLabel}` : ""}
                </li>
              ))}
            </ul>
          </div>
        ) : baggageLabel ? (
          <div className="hpm-summary__fact">
            <FaSuitcase aria-hidden className="hpm-summary__fact-icon" />
            <span style={{ whiteSpace: "pre-line" }}>{baggageLabel}</span>
          </div>
        ) : null}
      </div>

      {outbound || inbound ? (
        <div className="hpm-summary__flight">
          <div className="hpm-summary__flight-head">
            <FaPlane aria-hidden className="hpm-summary__fact-icon" />
            <div>
              <strong>{airlineLabel}</strong>
              {outbound ? (
                <span className="hpm-summary__flight-leg">
                  <em>הלוך</em>
                  {outbound.depart} {outbound.from}
                  {" → "}
                  {outbound.arrive} {outbound.to}
                </span>
              ) : null}
              {inbound ? (
                <span className="hpm-summary__flight-leg">
                  <em>חזור</em>
                  {inbound.depart} {inbound.from}
                  {" → "}
                  {inbound.arrive} {inbound.to}
                </span>
              ) : null}
            </div>
          </div>
          <button
            type="button"
            className="hpm-summary__flight-toggle"
            aria-expanded={flightDetailsOpen}
            onClick={() => setFlightDetailsOpen((v) => !v)}
          >
            פרטי הטיסות
          </button>
          {flightDetailsOpen ? (
            <div className="hpm-summary__flight-details">
              {flightOptionLabel ? (
                <p>
                  <strong>{flightOptionLabel}</strong>
                </p>
              ) : null}
              {outbound ? (
                <p>
                  הלוך: {outbound.airline} {outbound.flightNo} · {outbound.from}–
                  {outbound.to} · {outbound.depart}–{outbound.arrive}
                  {outbound.duration ? ` · ${outbound.duration}` : ""}
                </p>
              ) : null}
              {inbound ? (
                <p>
                  חזור: {inbound.airline} {inbound.flightNo} · {inbound.from}–
                  {inbound.to} · {inbound.depart}–{inbound.arrive}
                  {inbound.duration ? ` · ${inbound.duration}` : ""}
                </p>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}

      {Number.isFinite(customerIls) && customerIls > 0 ? (
        <>
          {showAgentBreakdown ? (
            <div className="hwz-summary-split">
              <dl className="hwz-summary-split__card" data-testid="hwz-agent-price">
                <dt>נטו לסוכן</dt>
                <dd>
                  <bdi dir="ltr">
                    {agentSearch?.ok && agentSearch.currency !== "ILS"
                      ? formatHubwayzSaleDisplay(agentSearch.amount, agentSearch.currency)
                      : formatIls(Math.round(agentNetIls))}
                    {agentSearch?.ok && agentSearch.currency !== "ILS" ? (
                      <small className="hwz-summary-split__ils">
                        ({formatIls(Math.round(agentNetIls))})
                      </small>
                    ) : null}
                  </bdi>
                </dd>
              </dl>
              <dl className="hwz-summary-split__card" data-testid="hwz-agent-commission">
                <dt>עמלת סוכן</dt>
                <dd>
                  <bdi dir="ltr">
                    {commissionSearch?.ok && commissionSearch.currency !== "ILS"
                      ? formatHubwayzSaleDisplay(
                          commissionSearch.amount,
                          commissionSearch.currency
                        )
                      : formatIls(Math.round(agentMarkupIls))}
                    {commissionSearch?.ok && commissionSearch.currency !== "ILS" ? (
                      <small className="hwz-summary-split__ils">
                        ({formatIls(Math.round(agentMarkupIls))})
                      </small>
                    ) : null}
                  </bdi>
                </dd>
              </dl>
            </div>
          ) : null}
          <div className="hwz-summary-pay">
            <span>סה״כ לתשלום הלקוח</span>
            <strong>
              {formatIls(shownIls)}
              {customerUsdLabel ? (
                <span className="hwz-summary-pay__usd" dir="ltr">
                  {" "}
                  ({customerUsdLabel})
                </span>
              ) : null}
            </strong>
            <small>שקלים</small>
          </div>
          {rateLabel ? (
            <p className="hpm-summary__fx-rate">
              שער המרות: <span dir="ltr">{rateLabel}</span>
            </p>
          ) : null}
        </>
      ) : (
        <p className="usm-fh-empty">ממתין למחיר מאומת.</p>
      )}
      {breakdown.length ? (
        <dl className="hpm-summary__breakdown">
          {breakdown.map((row) => (
            <div key={row.key}>
              <dt>{row.label}</dt>
              <dd>{row.valueText}</dd>
            </div>
          ))}
        </dl>
      ) : null}

      {showActions ? (
        <div className="hpm-summary__actions">
          {showTerms ? (
            <label className="hpm-terms">
              <input
                type="checkbox"
                checked={!!termsChecked}
                onChange={(e) => onTermsChange?.(e.target.checked)}
              />
              <span>
                אני מאשר/ת את{" "}
                <span className="hpm-terms__link">תנאי ההזמנה וגילוי נאות</span>
              </span>
            </label>
          ) : null}
          {disableHint ? (
            <p className="hpm-summary__terms-hint" role="status">
              {disableHint}
            </p>
          ) : null}

          <button
            type="button"
            className="hpm-btn-primary hpm-btn-primary--block"
            disabled={continueDisabled}
            title={disableHint || undefined}
            onClick={() => {
              if (continueDisabled) return;
              onPrimary?.();
            }}
          >
            {primaryLabel}
          </button>
          {onDeferred ? (
            <button
              type="button"
              className="hpm-btn-ghost hpm-btn-primary--block"
              disabled={continueDisabled}
              onClick={() => {
                if (continueDisabled) return;
                onDeferred();
              }}
            >
              {deferredLabel}
            </button>
          ) : null}
          {onSecondary ? (
            <button
              type="button"
              className="hpm-btn-ghost hpm-btn-primary--block"
              onClick={onSecondary}
            >
              {secondaryLabel}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
